import { Injectable } from '@angular/core';
import {
  EstimationWarning,
  EstimationWarningCode,
  isBlockingWarningCode
} from '../models/estimation-v3.types';
import { Task } from '../models/task.model';

export type WarningSeverity = 'blocking' | 'risk' | 'info';

/**
 * Centraliza la clasificación visual y semántica de los códigos de warning
 * del catálogo cerrado v3. Lo consumen el banner, el panel de warnings y
 * el componente principal de estimaciones.
 */
@Injectable({ providedIn: 'root' })
export class EstimationWarningsService {

  private static readonly SOURCE_SUBTYPE_LABELS: Record<string, string> = {
    industry_baseline: 'Estándar industria',
    inferred_gap: 'Vacío inferido',
    sdlc_standard: 'SDLC estándar',
    estimated_history: 'Histórica calibrada',
    execution_history: 'Ejecución histórica',
    execution_only: 'Tarea preventiva'
  };

  private static readonly RISK_CODES: ReadonlyArray<EstimationWarningCode> = [
    'LOW_HISTORICAL_SAMPLE',
    'STALE_HISTORICAL_CONTEXT',
    'LOW_SIMILARITY_CONTEXT',
    'LARGE_SCOPE_BUDGET_RISK',
    'DEADLINE_RISK'
  ];

  /** Clasifica la severidad UX de un código de warning. */
  getSeverity(code: EstimationWarningCode): WarningSeverity {
    if (isBlockingWarningCode(code)) return 'blocking';
    if (EstimationWarningsService.RISK_CODES.includes(code)) return 'risk';
    return 'info';
  }

  /** Texto legible para el usuario final. */
  getLabel(code: EstimationWarningCode): string {
    const labels: Record<EstimationWarningCode, string> = {
      PROMPT_INJECTION_BLOCKED: 'Intento de inyección bloqueado',
      PII_REDACTED: 'Datos personales redactados',
      SCOPE_INSUFFICIENT: 'Alcance insuficiente',
      OUT_OF_DOMAIN: 'Fuera del dominio software',
      LOW_HISTORICAL_SAMPLE: 'Muestra histórica reducida',
      STALE_HISTORICAL_CONTEXT: 'Histórico antiguo (>2 años)',
      LOW_SIMILARITY_CONTEXT: 'Análogos poco similares',
      ASSUMED_TECHNOLOGY: 'Tecnología asumida',
      ASSUMED_PROFILE: 'Perfil asumido',
      LARGE_SCOPE_BUDGET_RISK: 'Riesgo de presupuesto',
      DEADLINE_RISK: 'Riesgo de plazo',
      INFERRED_GAPS_ADDED: 'Vacíos técnicos añadidos',
      PHANTOM_TASKS_ADDED: 'Tareas preventivas añadidas',
      ROLLUP_RECALCULATED: 'Suma de horas reconciliada',
      OTHER: 'Aviso del sistema'
    };
    return labels[code] || 'Aviso desconocido';
  }

  /**
   * SVG path d="..." para representar visualmente cada warning.
   * Pensado para usarse dentro de <svg viewBox="0 0 24 24"> ... <path d="..."/></svg>.
   */
  getIconPath(code: EstimationWarningCode): string {
    const icons: Record<EstimationWarningCode, string> = {
      PROMPT_INJECTION_BLOCKED: 'M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm-2 16l-4-4 1.41-1.41L10 14.17l6.59-6.59L18 9l-8 8z',
      PII_REDACTED: 'M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z',
      SCOPE_INSUFFICIENT: 'M11 7h2v2h-2zm0 4h2v6h-2zm1-9C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z',
      OUT_OF_DOMAIN: 'M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z',
      LOW_HISTORICAL_SAMPLE: 'M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67z',
      STALE_HISTORICAL_CONTEXT: 'M13 3c-4.97 0-9 4.03-9 9H1l3.89 3.89.07.14L9 12H6c0-3.87 3.13-7 7-7s7 3.13 7 7-3.13 7-7 7c-1.93 0-3.68-.79-4.94-2.06l-1.42 1.42C8.27 19.99 10.51 21 13 21c4.97 0 9-4.03 9-9s-4.03-9-9-9zm-1 5v5l4.28 2.54.72-1.21-3.5-2.08V8H12z',
      LOW_SIMILARITY_CONTEXT: 'M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z',
      ASSUMED_TECHNOLOGY: 'M9.4 16.6L4.8 12l4.6-4.6L8 6l-6 6 6 6 1.4-1.4zm5.2 0l4.6-4.6-4.6-4.6L16 6l6 6-6 6-1.4-1.4z',
      ASSUMED_PROFILE: 'M12 5.9c1.16 0 2.1.94 2.1 2.1s-.94 2.1-2.1 2.1S9.9 9.16 9.9 8s.94-2.1 2.1-2.1m0 9c2.97 0 6.1 1.46 6.1 2.1v1.1H5.9V17c0-.64 3.13-2.1 6.1-2.1M12 4C9.79 4 8 5.79 8 8s1.79 4 4 4 4-1.79 4-4-1.79-4-4-4zm0 9c-2.67 0-8 1.34-8 4v3h16v-3c0-2.66-5.33-4-8-4z',
      LARGE_SCOPE_BUDGET_RISK: 'M11.8 10.9c-2.27-.59-3-1.2-3-2.15 0-1.09 1.01-1.85 2.7-1.85 1.78 0 2.44.85 2.5 2.1h2.21c-.07-1.72-1.12-3.3-3.21-3.81V3h-3v2.16c-1.94.42-3.5 1.68-3.5 3.61 0 2.31 1.91 3.46 4.7 4.13 2.5.6 3 1.48 3 2.41 0 .69-.49 1.79-2.7 1.79-2.06 0-2.87-.92-2.98-2.1h-2.2c.12 2.19 1.76 3.42 3.68 3.83V21h3v-2.15c1.95-.37 3.5-1.5 3.5-3.55 0-2.84-2.43-3.81-4.7-4.4z',
      DEADLINE_RISK: 'M9 11H7v2h2v-2zm4 0h-2v2h2v-2zm4 0h-2v2h2v-2zm2-7h-1V2h-2v2H8V2H6v2H5c-1.11 0-1.99.9-1.99 2L3 20c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 16H5V9h14v11z',
      INFERRED_GAPS_ADDED: 'M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z',
      PHANTOM_TASKS_ADDED: 'M12 2C8.13 2 5 5.13 5 9c0 1.74.5 3.37 1.41 4.84.95 1.54 2.2 2.86 3.16 4.4.47.75.81 1.45 1.17 2.26.26.55.47 1.5 1.26 1.5s1-.95 1.25-1.5c.37-.81.7-1.51 1.17-2.26.96-1.53 2.21-2.85 3.16-4.4C18.5 12.37 19 10.74 19 9c0-3.87-3.13-7-7-7zm0 9.75c-1.52 0-2.75-1.23-2.75-2.75s1.23-2.75 2.75-2.75 2.75 1.23 2.75 2.75-1.23 2.75-2.75 2.75z',
      ROLLUP_RECALCULATED: 'M19 3H5c-1.11 0-2 .9-2 2v14c0 1.1.89 2 2 2h14c1.11 0 2-.9 2-2V5c0-1.1-.89-2-2-2zM8 17H6v-2h2v2zm0-4H6v-2h2v2zm0-4H6V7h2v2zm5 8h-2v-2h2v2zm0-4h-2v-2h2v2zm0-4h-2V7h2v2zm5 8h-2v-2h2v2zm0-4h-2v-2h2v2zm0-4h-2V7h2v2z',
      OTHER: 'M11 9h2V7h-2v2zm1-7C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm-1-3h2v-6h-2v6z'
    };
    return icons[code] || icons.OTHER;
  }

  /** True si entre los warnings hay alguno bloqueante. */
  isBlocking(warnings: EstimationWarning[] | undefined): boolean {
    if (!warnings || warnings.length === 0) return false;
    return warnings.some(w => isBlockingWarningCode(w.code));
  }

  /** Devuelve el primer warning bloqueante (útil para mostrar el motivo principal). */
  getFirstBlocking(warnings: EstimationWarning[] | undefined): EstimationWarning | null {
    if (!warnings || warnings.length === 0) return null;
    return warnings.find(w => isBlockingWarningCode(w.code)) ?? null;
  }

  /** Etiqueta legible para un source_subtype del catálogo cerrado. */
  getSourceSubtypeLabel(subtype: string | undefined | null): string {
    if (!subtype) return '';
    return EstimationWarningsService.SOURCE_SUBTYPE_LABELS[subtype] || subtype;
  }

  /**
   * Construye el tooltip multi-línea con los factores aplicados a una tarea v3.
   * Solo incluye los campos que existen. Si la tarea no tiene metadata v3,
   * devuelve cadena vacía (el tooltip no se muestra).
   */
  formatTaskFactorsTooltip(task: Task | undefined | null): string {
    if (!task) return '';
    const lines: string[] = [];

    if (task.confidence) {
      lines.push(`Confianza: ${task.confidence}`);
    }
    if (typeof task.ai_assistance_factor === 'number') {
      lines.push(`Factor IA: ${task.ai_assistance_factor.toFixed(2)}`);
    }
    if (typeof task.calibration_factor_applied === 'number') {
      lines.push(`Calibración: ×${task.calibration_factor_applied.toFixed(2)}`);
    }
    if (typeof task.freshness_weight_applied === 'number') {
      lines.push(`Peso antigüedad: ${task.freshness_weight_applied.toFixed(2)}`);
    }
    if (typeof task.p10_hours === 'number' && typeof task.p90_hours === 'number') {
      lines.push(`Rango: ${task.p10_hours}h – ${task.p90_hours}h`);
    }
    if (task.analog_project_ids && task.analog_project_ids.length > 0) {
      lines.push(`Análogos: ${task.analog_project_ids.join(', ')}`);
    }

    return lines.join(' · ');
  }
}